/**
 * The browser's calls to the Stage 5 endpoints.
 *
 * SAME DISCIPLINE AS THE SESSION GATEWAY. The browser holds no token; the
 * session is an `HttpOnly` cookie the API set, carried because every call is
 * same-origin. Nothing here names a tenant, because there is nothing it could
 * name one with — the server takes the tenant from the session and ignores
 * anything a request says about it.
 *
 * EVERY UNSAFE CALL CARRIES AN `Idempotency-Key`, generated here, and that is
 * the point of generating it here rather than on the server: a key the server
 * minted would be a different key on every retry, which is the same as having
 * none. The key is per ACTION, so a double-clicked Approve button sends the
 * same key twice and the second one replays the first's answer.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly detail: string | undefined;

  constructor(status: number, title: string, detail?: string | undefined) {
    super(title);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
  }
}

function newKey(): string {
  // `crypto.randomUUID` in every browser this application supports. The
  // fallback is not security-sensitive — an idempotency key needs to be
  // UNIQUE, not unguessable, because it is only ever scoped to the caller's
  // own principal and can therefore collide with nobody else's.
  const uuid = globalThis.crypto?.randomUUID?.();
  return uuid ?? `${String(Date.now())}-${String(Math.random()).slice(2)}`;
}

export interface TaskSummary {
  readonly id: string;
  readonly title: string;
  readonly description: string;
  readonly sourceModule: string;
  readonly assigneeUserId: string | null;
  readonly priority: string;
  readonly status: string;
  readonly dueAt: string | null;
  readonly createdAt: string;
  readonly version: number;
}

export interface ActivityEntry {
  readonly id: string;
  readonly activityType: string;
  readonly origin: string;
  readonly actorId: string | null;
  readonly occurredAt: string;
}

export interface CommentEntry {
  readonly id: string;
  readonly parentId: string | null;
  readonly body: string;
  readonly deleted: boolean;
  readonly authorId: string;
  readonly createdAt: string;
}

export interface TaskDetail {
  readonly task: TaskSummary;
  readonly assignments: ReadonlyArray<{
    readonly id: string;
    readonly kind: string;
    readonly toUserId: string | null;
    readonly note: string | null;
    readonly occurredAt: string;
  }>;
  readonly watchers: ReadonlyArray<{ readonly userId: string; readonly reason: string }>;
  readonly activity: readonly ActivityEntry[];
}

export interface ApprovalSummary {
  readonly approvalId: string;
  readonly instanceId: string;
  readonly stepInstanceId: string;
  readonly stepName: string;
  readonly subjectResource: string;
  readonly subjectRecordId: string;
  readonly dueAt: string | null;
  readonly openedAt: string;
}

export interface ApprovalDetail {
  readonly instance: {
    readonly id: string;
    readonly subjectResource: string;
    readonly subjectRecordId: string;
    readonly state: string;
    readonly currentStepNumber: number | null;
  };
  readonly steps: ReadonlyArray<{
    readonly stepInstanceId: string;
    readonly stepNumber: number;
    readonly state: string;
    readonly decisions: ReadonlyArray<{
      readonly approverUserId: string;
      readonly decision: string;
      readonly comment: string | null;
    }>;
  }>;
  readonly history: ReadonlyArray<{
    readonly id: string;
    readonly fromState: string | null;
    readonly toState: string;
    readonly action: string;
    readonly actorType: string;
    readonly occurredAt: string;
  }>;
  readonly activity: readonly ActivityEntry[];
}

export interface WorkGateway {
  myTasks(): Promise<readonly TaskSummary[]>;
  task(taskId: string): Promise<TaskDetail>;
  completeTask(taskId: string, key: string): Promise<void>;
  approvals(): Promise<readonly ApprovalSummary[]>;
  approval(instanceId: string): Promise<ApprovalDetail>;
  decide(
    instanceId: string,
    input: {
      readonly decision: 'approved' | 'rejected' | 'sent_back';
      readonly comment?: string | undefined;
      readonly expectedStepInstanceId?: string | undefined;
    },
    key: string,
  ): Promise<{ readonly instanceState: string; readonly changed: boolean }>;
  comments(resource: string, recordId: string): Promise<readonly CommentEntry[]>;
  comment(
    resource: string,
    recordId: string,
    body: string,
    key: string,
  ): Promise<{ readonly unresolvedHandles: readonly string[] }>;
  activity(resource: string, recordId: string): Promise<readonly ActivityEntry[]>;
  /** A fresh idempotency key, held by the caller for the life of one action. */
  key(): string;
}

export function createWorkGateway(baseUrl = '/api/v1'): WorkGateway {
  async function call<T>(path: string, init?: RequestInit & { readonly key?: string }): Promise<T> {
    const headers: Record<string, string> = { 'content-type': 'application/json' };
    if (init?.key !== undefined) headers['idempotency-key'] = init.key;

    const response = await fetch(`${baseUrl}${path}`, {
      credentials: 'same-origin',
      ...init,
      headers: { ...headers, ...(init?.headers as Record<string, string> | undefined) },
    });

    if (!response.ok) {
      // RFC 7807 from the API. Read rather than guessed, so the screen can show
      // what the server actually said instead of "something went wrong".
      const problem = (await response.json().catch(() => ({}))) as {
        title?: string;
        detail?: string;
      };
      throw new ApiError(
        response.status,
        problem.title ?? `Request failed (${String(response.status)})`,
        problem.detail,
      );
    }
    return (await response.json()) as T;
  }

  return {
    key: newKey,
    async myTasks() {
      const { tasks } = await call<{ tasks: TaskSummary[] }>('/tasks?status=open,in_progress');
      return tasks;
    },
    task: (taskId) => call<TaskDetail>(`/tasks/${taskId}`),
    async completeTask(taskId, key) {
      await call(`/tasks/${taskId}/complete`, { method: 'POST', body: '{}', key });
    },
    async approvals() {
      const { approvals } = await call<{ approvals: ApprovalSummary[] }>('/approvals');
      return approvals;
    },
    approval: (instanceId) => call<ApprovalDetail>(`/approvals/${instanceId}`),
    decide: (instanceId, input, key) =>
      call(`/approvals/${instanceId}/decision`, {
        method: 'POST',
        body: JSON.stringify(input),
        key,
      }),
    async comments(resource, recordId) {
      const { comments } = await call<{ comments: CommentEntry[] }>(
        `/records/${resource}/${recordId}/comments`,
      );
      return comments;
    },
    comment: (resource, recordId, body, key) =>
      call(`/records/${resource}/${recordId}/comments`, {
        method: 'POST',
        body: JSON.stringify({ body }),
        key,
      }),
    async activity(resource, recordId) {
      const { activity } = await call<{ activity: ActivityEntry[] }>(
        `/records/${resource}/${recordId}/activity`,
      );
      return activity;
    },
  };
}
